import CTNButton from '@ctn/ui/components/ctn-button';
import CTNEllipsis from '@ctn/ui/components/ctn-ellipsis';

import { SUBSANACION_ESTADO_COLOR } from '@/constants/tramite.constants';
import { getIconDocumento } from '@/helpers/upload';
import { type IDocumento, type ISolicitudDocumentacion } from '@/interfaces/model/don.interface';
import type { CustomColumnDef } from '@ctn/ui/types/ctn-table';

import TextIcon from '../TextIcon';

const setColumn = <K extends keyof ISolicitudDocumentacion>(
	key: K,
	header: string,
	width?: number,
	customCell?: CustomColumnDef<ISolicitudDocumentacion>['cell']
): CustomColumnDef<ISolicitudDocumentacion> => ({
	accessorKey: key as string,
	header,
	enableSorting: false,
	...(width ? { th: { width } } : {}),
	responsive: { sizeColumn: 'full' },
	cell: customCell ?? (({ getValue }) => <CTNEllipsis content={getValue() as string} />),
});

const getColumnsTramite = () => {
	const columns: Array<CustomColumnDef<ISolicitudDocumentacion>> = [
		setColumn('fechaCreacion', 'Fecha alta', 164),
		setColumn('observacionesPeticion', 'Motivo de solicitud'),
		setColumn('fechaSubsanacion', 'Fecha de respuesta', 164),
		setColumn('tipoDocumento', 'Tipo', undefined, ({ getValue }) => {
			const { descripcion } = (getValue() as ISolicitudDocumentacion['tipo']) || {
				tipo: 0,
				descripcion: '-',
			};
			return <CTNEllipsis content={descripcion} />;
		}),
		setColumn('estado', 'Estado', 164, ({ getValue }) => {
			const { id, descripcion } = getValue() as ISolicitudDocumentacion['estado'];
			return (
				<TextIcon
					description={descripcion}
					iconLeft={{
						icon: 'status',
						color: SUBSANACION_ESTADO_COLOR[id as keyof typeof SUBSANACION_ESTADO_COLOR] ?? 'gray-50',
					}}
				/>
			);
		}),
	];

	return columns;
};

const getColumnsPendiente = ({
	onSendDocumentacion,
}: {
	onSendDocumentacion?: (value: ISolicitudDocumentacion) => void;
}) => {
	const columns: Array<CustomColumnDef<ISolicitudDocumentacion>> = [
		setColumn('fechaCreacion', 'Fecha alta', 164),
		setColumn('tramite', 'Nº solicitud', 164, ({ getValue }) => {
			const { numeroTramite } = getValue() as ISolicitudDocumentacion['tramite'];
			return <CTNEllipsis content={numeroTramite} />;
		}),
		setColumn('tipoDocumento', 'Tipo Documento', 180, ({ row: { original } }) => {
			const value = original?.tipoDocumento?.descripcion;
			return <CTNEllipsis content={value || '-'} />;
		}),
		setColumn('observacionesPeticion', 'Motivo de solicitud'),
		{
			header: '',
			accessorKey: 'actions',
			enableSorting: false,
			th: { width: 186 },
			td: { align: 'center' },
			responsive: { sizeColumn: 'full' },
			cell({ row }) {
				return (
					<CTNButton
						variant="ghost"
						mode="secondary"
						size="size-32"
						label="Enviar documento"
						onClick={() => onSendDocumentacion?.(row.original)}
					/>
				);
			},
		},
	];

	return columns;
};

const getColumnsAtendidas = ({ onViewDetail }: { onViewDetail?: (value: ISolicitudDocumentacion) => void }) => {
	const columns: Array<CustomColumnDef<ISolicitudDocumentacion>> = [
		setColumn('fechaCreacion', 'Fecha alta', 164),
		setColumn('tramite', 'Nº solicitud', 164, ({ getValue }) => {
			const { numeroTramite } = getValue() as ISolicitudDocumentacion['tramite'];
			return <CTNEllipsis content={numeroTramite} />;
		}),
		setColumn('tipoDocumento', 'Tipo Documento', 180, ({ row: { original } }) => {
			const value = original?.tipoDocumento?.descripcion;
			return <CTNEllipsis content={value || '-'} />;
		}),
		setColumn('fechaSubsanacion', 'Fecha envío', 164),
		setColumn('documentoSubsanado', 'Documento', undefined, ({ getValue }) => {
			const value = getValue() as IDocumento;
			return value && <TextIcon iconLeft={{ icon: getIconDocumento(value) }} description={value?.nombre} />;
		}),
		{
			header: '',
			enableSorting: false,
			accessorKey: 'actions',
			th: { width: 110 },
			td: { align: 'center' },
			responsive: { sizeColumn: 'full' },
			cell({ row }) {
				return (
					<CTNButton
						variant="ghost"
						mode="secondary"
						size="size-32"
						label="Ver detalle"
						onClick={() => onViewDetail?.(row.original)}
					/>
				);
			},
		},
	];

	return columns;
};

export { getColumnsAtendidas, getColumnsPendiente, getColumnsTramite };